import { useEffect, useState } from 'react';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { useStripe, useElements, PaymentElement, Elements } from '@stripe/react-stripe-js';
import { StripeElementsOptions } from '@stripe/stripe-js';
import { InfoCircleFill } from 'react-bootstrap-icons';
import { Container, Row, Col } from 'react-bootstrap';
import { Api, SetupIntentCreateResp } from './Data/Api';
import { Facility, ParkerPlanInvite } from './Data/ApiTypes';
import { stripePromise } from './Data/ApiStripe';
import { connectUrl } from './Data/WebUrl';
import { Button } from './Controls/Button';
import { Hyperlink } from './Controls/Hyperlink';
import { TermsModal } from './Controls/TermsModal';
import { appearanceOptions, stripeFonts } from './Controls/StripeAppearance';
import { PageMaskOpts, maskAtom } from './Data/Atoms';
import { useAtom } from 'jotai';
import { processResult } from './Data/Result';

export interface UsageBasedInviteLoaderData {
    inviteId: number;
    invite:   ParkerPlanInvite;
    facility: Facility;
}

export function CheckoutPageUsageBasedInvite() {
    const { inviteId, invite, facility } = useLoaderData() as UsageBasedInviteLoaderData;
    const [createResp, setCreateResp]   = useState<SetupIntentCreateResp>();
    const [err, setErr]                 = useState<string>();
    const [paymentVis, setPaymentVis]   = useState<'hidden' | 'visible'>('hidden');
    const [terms, setTerms]             = useState<string>();
    const [showTerms, setShowTerms]     = useState(false);

    useEffect( () => {
        Api.usageBasedInviteSetupPayment( inviteId )
            .then( res => {
                if ( !res.isOk ) {
                    setErr( res.error );
                    return;
                }
                setCreateResp( res.value );
                setTimeout( () => setPaymentVis( 'visible' ), 500 );
            } );
    }, [] );

    useEffect( () => {
        Api.planTermsGet( invite.listedPlan.planId ).then(
            res => processResult(
                res,
                val => setTerms( val ),
                err => console.error( `Didn't retrieve terms because: ${err}` )
            )
        );
    }, [invite.listedPlan.planId] );

    const options: StripeElementsOptions = {
        clientSecret:                createResp?.clientSecret,
        customerSessionClientSecret: createResp?.customerSessionClientSecret,
        fonts:                       stripeFonts,
        appearance:                  appearanceOptions,
    };

    return (
        <Container className="py-4">
            <TermsModal
                terms={terms ?? "Error!"}
                show={showTerms}
                setShow={setShowTerms}
            />
            <Row className="justify-content-center">
                <Col xs={12} md={8} lg={6}>
                    <p className="text-muted text-uppercase fw-semibold" style={{ fontSize: '0.75rem', letterSpacing: '0.08em' }}>
                        Enrollment Invitation
                    </p>
                    <h1 className="h2 fw-bold mb-3">Set up automatic billing for your parking</h1>

                    <p className="mb-4">
                        <strong>{facility.name}</strong> has invited you to enroll in usage-based billing.
                        Save a card below to get started &mdash; you won&rsquo;t be charged anything today.
                    </p>

                    <div className="rounded p-3 mb-4 d-flex gap-2"
                         style={{ backgroundColor: '#fff0f8', border: '1px solid #ffb3e0' }}>
                        <InfoCircleFill className="flex-shrink-0 mt-1" color="#D61F84" size={16} />
                        <div>
                            <p className="fw-semibold mb-1" style={{ fontSize: '0.9rem' }}>Authorization &mdash; please read</p>
                            <p className="mb-0" style={{ fontSize: '0.85rem' }}>
                                By saving a card, you authorize <strong>{facility.name}</strong> to
                                charge your card for parking activity in their lot.
                                Charges are applied automatically based on your actual usage and the rates posted
                                at the facility. You will receive a receipt when a charge is applied.
                                {' '}Enrollment is subject to the plan{' '}
                                <Hyperlink onClick={() => setShowTerms(true)}>Terms and Conditions</Hyperlink>.
                            </p>
                        </div>
                    </div>

                    {err && (
                        <div className="alert alert-danger">{err}</div>
                    )}

                    {paymentVis === 'hidden' && !err && (
                        <div className="text-secondary mb-3">Loading&hellip;</div>
                    )}

                    <div style={{ visibility: paymentVis }}>
                        {createResp && (
                            <Elements stripe={stripePromise} options={options}>
                                <UsageBasedPaymentForm inviteId={inviteId} facilityName={facility.name} />
                            </Elements>
                        )}
                    </div>
                </Col>
            </Row>
        </Container>
    );
}

interface UsageBasedPaymentFormProps {
    inviteId:     number;
    facilityName: string;
}

function UsageBasedPaymentForm( { inviteId, facilityName }: UsageBasedPaymentFormProps ) {
    const stripe   = useStripe();
    const elements = useElements();
    const nav      = useNavigate();
    const [, setMask] = useAtom( maskAtom );

    const maskOptions: PageMaskOpts = {
        showMask: true,
        header:   <div className="h2">Processing</div>,
        subText:  'Do not close your browser',
    };

    const handleSubmit = async ( e: React.FormEvent ) => {
        e.preventDefault();
        if ( !stripe || !elements ) return;

        setMask( maskOptions );

        const result = await stripe.confirmSetup( {
            elements,
            confirmParams: {
                return_url: connectUrl(
                    `/checkout/usage-based/invite/success?invite=${inviteId}`
                ),
            },
        } );

        setMask( undefined );

        if ( result.error ) {
            console.error( result.error.message );
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <PaymentElement />
            <div className="d-flex justify-content-end mt-3 gap-2">
                <Button type="button" className="px-3" onClick={ () => nav( -1 ) }>
                    Cancel
                </Button>
                <Button type="submit" disabled={!stripe} className="px-3 btn-main">
                    Save and Enroll
                </Button>
            </div>
        </form>
    );
}
